import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useGetList, useListContext, useTranslate } from 'react-admin';
import { useChart } from 'src/components/chart';
import { fShortenNumber } from 'src/utils/format-number';
import { PredictionByDayItem, predictionDashboardResources } from '../dataProvider';
import { PredictionChartCard } from './PredictionChartCard';
import {
  formatPredictionCurrencyTooltipValue,
  formatPredictionTooltipDate,
  formatPredictionXAxisLabel,
  getPredictionLast30DayRange,
  PredictionChartPointContext,
} from './prediction-chart.helpers';

export const PredictionTradingActivityChart = () => {
  const { filterValues } = useListContext();
  const translate = useTranslate();
  const theme = useTheme();

  const tradingType = filterValues?.tradingType;
  const { fromTime, toTime } = getPredictionLast30DayRange();
  const isPrediction = tradingType === 'Predict';
  const { data = [], isLoading: loading } = useGetList<PredictionByDayItem>(
    predictionDashboardResources.byDay,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'date', order: 'ASC' },
      filter: {
        fromTime,
        toTime,
      },
    },
    {
      enabled: isPrediction,
    }
  );

  const chart = useMemo(() => {
    const items = data ?? [];

    return {
      categories: items.map((item) => item.date),
      series: [
        {
          name: translate('module.predictionDashboard.tradeCount', { _: 'Trade Count' }),
          type: 'bar' as const,
          data: items.map((item) => Number(item.tradeCount ?? 0)),
        },
        {
          name: translate('module.predictionDashboard.notional', { _: 'Notional' }),
          type: 'line' as const,
          data: items.map((item) => Number(item.notional ?? 0)),
        },
      ],
    };
  }, [data, translate]);

  const chartOptions = useChart({
    chart: {
      height: 360,
      stacked: false,
    },
    stroke: {
      width: [0, 3],
      curve: 'smooth',
    },
    plotOptions: {
      bar: {
        columnWidth: '42%',
      },
    },
    colors: [theme.palette.primary.main, theme.palette.warning.main],
    legend: {
      show: true,
    },
    xaxis: {
      categories: chart.categories,
      labels: {
        rotate: -45,
        rotateAlways: true,
        formatter: formatPredictionXAxisLabel,
      },
    },
    yaxis: [
      {
        title: {
          text: translate('module.predictionDashboard.tradeCount', { _: 'Trade Count' }),
          style: { color: theme.palette.primary.main },
        },
        labels: {
          formatter: (value: number) => fShortenNumber(value),
          style: { colors: theme.palette.primary.main },
        },
      },
      {
        opposite: true,
        title: {
          text: translate('module.predictionDashboard.notional', { _: 'Notional' }),
          style: { color: theme.palette.warning.main },
        },
        labels: {
          formatter: (value: number) => `$${fShortenNumber(value)}`,
          style: { colors: theme.palette.warning.main },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (value: string | number, context?: PredictionChartPointContext) =>
          formatPredictionTooltipDate(chart.categories, value, context),
      },
      y: {
        formatter: formatPredictionCurrencyTooltipValue,
      },
    },
  });

  if (!isPrediction) return null;

  return (
    <PredictionChartCard
      title={translate('module.predictionDashboard.last30DaysTradingActivity', { _: 'Last 30 days trading activity' })}
      subheader={translate('module.predictionDashboard.tradingActivitySubheader', {
        _: 'Bar shows trade count, line shows notional by day for the last 30 days',
      })}
      loading={loading}
      hasData={chart.categories.length > 0}
      chartType="line"
      series={chart.series}
      options={chartOptions}
      emptyTitle={translate('common.noData', { _: 'No data' })}
    />
  );
};
